import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_V1_BASE_URL } from '../config/api';

const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { email?: string } | null;

    const [email, setEmail] = useState(locationState?.email || '');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setError('Please enter your email');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_V1_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: trimmedEmail }),
            });

            const result = (await response.json().catch(() => null)) as
                | { success: boolean; message?: string }
                | null;

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Something went wrong. Please try again.');
            }

            setSent(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unable to send reset email.');
        } finally {
            setIsLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 p-8">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Check your email</h2>
                    <p className="text-gray-600">
                        If an account exists for {email}, we've sent a link to reset your password.
                    </p>
                    <button
                        onClick={() => navigate('/login')}
                        className="mt-6 text-blue-600 hover:text-blue-700 font-medium"
                    >
                        Back to sign in
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 p-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Reset your password</h2>
                <p className="text-gray-600 mb-6">Enter your email and we'll send you a reset link.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                            placeholder="Enter your email"
                            required
                            autoComplete="username"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="w-full text-sm text-gray-600 hover:text-gray-800"
                    >
                        Back to sign in
                    </button>
                </form>
            </div>

            <p className="mt-8 text-center text-xs text-gray-500 w-full max-w-md mx-auto">
                Secured by enterprise-grade encryption
            </p>
        </div>
    );
};

export default ForgotPassword;